/**
 * NotificationPayVideoItem
 *
 * Covers the premium/boost video payment types (premium_content leading icon,
 * no avatar/user tap). Subtitle: `{charge} <coin-icon> · {date}`.
 *   - payPremiumVideo       — `notification_pay_premium_video_item.dart:29-30`
 *                             (charge = `mediaInfo.premiumVideoCharge`)
 *   - payPremiumVideoFailed — `notification_pay_premium_video_failed_item.dart:29`
 *   - payBoostVideo         — `notification_pay_boost_video_item.dart:29-30`
 *                             (charge = `mediaInfo.boostVideoCharge`)
 *   - payBoostVideoFailed   — `notification_pay_boost_video_failed_item.dart:29`
 *
 * They share one component varying by title l10n key + which charge field feeds
 * the subtitle. Trailing thumbnail from `mediaInfo`.
 */
import React from 'react';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';
import { MathUtils } from '@/domain/utils/mathUtils';
import { NotificationPayVideoKind } from '@/domain/enums/inbox/notificationPayVideoKind';
import { NotificationRowLayout } from '../NotificationRowLayout';
import { NotificationLeadingIcon } from './shared/NotificationLeadingIcon';
import { NotificationMediaThumbnail } from './shared/NotificationMediaThumbnail';
import { NotificationCoinsDateLine } from './shared/NotificationCoinsDateLine';
import type { NotificationItemProps } from './notificationItemProps';

interface NotificationPayVideoItemProps extends NotificationItemProps {
  kind: NotificationPayVideoKind;
}

export const NotificationPayVideoItem: React.FC<NotificationPayVideoItemProps> = ({
  kind,
  notification,
  onItemTap,
  onItemMediaTap,
}) => {
  const { l10n } = useLocale();
  const { formatDate } = useDateFormatters();
  const n = l10n.inbox.notifications;

  const { info } = notification;
  const mediaInfo = info.mediaInfo;

  const isBoost =
    kind === NotificationPayVideoKind.payBoostVideo ||
    kind === NotificationPayVideoKind.payBoostVideoFailed;

  let title: string;
  switch (kind) {
    case NotificationPayVideoKind.payPremiumVideo:
      title = n.payPremiumVideoSuccess;
      break;
    case NotificationPayVideoKind.payPremiumVideoFailed:
      title = n.payPremiumVideoFailed;
      break;
    case NotificationPayVideoKind.payBoostVideo:
      title = n.payBoostVideoSuccess;
      break;
    case NotificationPayVideoKind.payBoostVideoFailed:
      title = n.payBoostVideoFailed;
      break;
  }

  const charge = isBoost
    ? (mediaInfo?.boostVideoCharge ?? 0)
    : (mediaInfo?.premiumVideoCharge ?? 0);

  return (
    <NotificationRowLayout
      leading={<NotificationLeadingIcon iconPath="/assets/images/premium_content.png" />}
      title={title}
      subtitle={
        <NotificationCoinsDateLine
          amount={MathUtils.removeTrailingZero(charge)}
          date={formatDate(info.timestamp)}
        />
      }
      trailing={mediaInfo ? <NotificationMediaThumbnail mediaInfo={mediaInfo} /> : undefined}
      onItemTap={onItemTap ? () => onItemTap(notification) : undefined}
      onTrailingTap={onItemMediaTap ? () => onItemMediaTap(notification) : undefined}
    />
  );
};
